import { constructQueryParamName } from '../../util/search';

const findCategory = (categories, id) => {
  for (const category of categories || []) {
    if (category.id === id) return category;
    const nested = findCategory(category.subcategories, id);
    if (nested) return nested;
  }
  return null;
};

const optionLabel = (intl, config, value) => {
  const normalizedValue = String(value).replace(/^has_(all|any):/, '');
  return normalizedValue
    .split(',')
    .map(option => {
      const found = config?.enumOptions?.find(item => String(item.option) === option);
      return intl.formatMessage(
        {
          id: `ListingPage.field.${config?.key}.option.${option}`,
          defaultMessage: found?.label || option,
        }
      );
    })
    .join(', ');
};

/** Convert active URL filters into localized, removable saved-search chips. */
export const createSavedSearchCriteria = ({ selectedFilters, filterConfigs, intl, currency }) => {
  const listingFields = filterConfigs.listingFieldsConfig || [];
  const defaultFilters = filterConfigs.defaultFiltersConfig || [];
  const categories = filterConfigs.listingCategories || [];
  const ignoredKeys = new Set(['sort', 'page', 'mapSearch', 'origin', 'bounds']);

  return Object.entries(selectedFilters || {})
    .filter(([key, value]) => !ignoredKeys.has(key) && value !== '' && value != null)
    .map(([key, rawValue]) => {
      if (key === 'keywords') {
        return {
          key,
          labelMessageId: 'SavedSearchAlertCard.keywordChip',
          label: intl.formatMessage({ id: 'SavedSearchAlertCard.keywordChip' }),
          rawValue: String(rawValue),
          value: String(rawValue),
        };
      }
      if (key === 'address') {
        return {
          key,
          labelMessageId: 'SavedSearchAlertCard.locationChip',
          label: intl.formatMessage({ id: 'SavedSearchAlertCard.locationChip' }),
          rawValue: String(rawValue),
          value: String(rawValue),
        };
      }
      if (key === 'price') {
        const [min, max] = String(rawValue).split(',');
        const format = value =>
          value === ''
            ? ''
            : intl.formatNumber(Number(value), {
                style: 'currency',
                currency,
                maximumFractionDigits: 0,
              });
        return {
          key,
          labelMessageId: 'FilterComponent.priceLabel',
          label: intl.formatMessage({ id: 'FilterComponent.priceLabel' }),
          rawValue: String(rawValue),
          value: [format(min), format(max)].filter(Boolean).join(' – '),
        };
      }
      if (key.startsWith('pub_categoryLevel')) {
        const category = findCategory(categories, String(rawValue));
        return {
          key,
          labelMessageId: 'FilterComponent.categoryLabel',
          label: intl.formatMessage({ id: 'FilterComponent.categoryLabel' }),
          rawValue: String(rawValue),
          value: intl.formatMessage({
            id: `Category.${rawValue}`,
            defaultMessage: category?.name || category?.label || String(rawValue),
          }),
        };
      }

      const config = [...listingFields, ...defaultFilters].find(item => {
        const queryName = constructQueryParamName(item.key, item.scope);
        return queryName === key || item.key === key;
      });
      const fallbackLabel = config?.filterConfig?.label || config?.label || config?.key || key;
      return {
        key,
        labelMessageId: `FilterComponent.${config?.key}Label`,
        label: intl.formatMessage({
          id: `FilterComponent.${config?.key}Label`,
          defaultMessage: fallbackLabel,
        }),
        rawValue: String(rawValue),
        value: optionLabel(intl, config, rawValue),
      };
    });
};
